import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStateSelection } from '../contexts/StateContext';
import Header from '../components/Header';

const LicensingGuide: React.FC = () => {
  const { userProfile } = useAuth();
  const { state: contextState } = useStateSelection();
  
  // Use user's profile state (DB), then selectedState, context, or re_state; default ga
  const currentState = userProfile?.state?.toLowerCase() || localStorage.getItem('selectedState')?.toLowerCase() || contextState?.toLowerCase() || localStorage.getItem('re_state')?.toLowerCase() || 'ga';
  const isFlorida = currentState === 'fl';
  const isCalifornia = currentState === 'ca';
  const isTexas = currentState === 'tx';
  const isNewYork = currentState === 'ny';
  const isNewJersey = currentState === 'nj';

  return (
    <div className="min-h-screen bg-gray-50">
      <Header 
        userMode="full" 
        onToggleDarkMode={() => {}} 
        userProfile={userProfile}
        darkMode={false}
      />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header Section */}
          <div className={`bg-gradient-to-r ${isFlorida ? 'from-orange-600 to-red-600' : isCalifornia ? 'from-yellow-600 to-orange-600' : isTexas ? 'from-red-600 to-blue-600' : isNewYork ? 'from-blue-600 to-indigo-600' : isNewJersey ? 'from-emerald-600 to-teal-600' : 'from-blue-600 to-purple-600'} text-white p-8`}>
            <h1 className="text-4xl font-bold mb-4">
              Your Path to a {isFlorida ? 'Florida' : isCalifornia ? 'California' : isTexas ? 'Texas' : isNewYork ? 'New York' : isNewJersey ? 'New Jersey' : 'Georgia'} Real Estate License
            </h1>
            <p className="text-xl text-blue-100">
              Whether you're just starting your flashcard prep or have already completed it, this is a great foundation for your real estate journey. Here are the required steps you must take to get your license.
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="space-y-8">
              {isFlorida ? (
                // Florida Content
                <>
                  {/* Step 1 - Florida */}
                  <div className="border-l-4 border-orange-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">1</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete the 63-Hour Pre-Licensing Course</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      The state of Florida requires all aspiring sales associates to complete a 63-hour course from a Florida Real Estate Commission (FREC) approved school.
                    </p>
                    
                    <div className="bg-orange-50 p-4 rounded-lg mb-4">
                      <h3 className="text-lg font-semibold text-orange-800 mb-2">Find an Approved School:</h3>
                      <p className="text-gray-700 mb-3">
                        You must choose a school from the official FREC-approved list to ensure your course is valid.
                      </p>
                      <div className="bg-white p-3 rounded border">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Official FREC List:</strong> Check the official, up-to-date list of all approved schools directly from the source.
                        </p>
                        <a 
                          href="https://www.myfloridalicense.com/CheckLicense2/Default.aspx" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-orange-600 hover:text-orange-800 font-medium"
                        >
                          📄 Find Approved Schools Here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Popular FREC-Approved Online Providers:</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <a 
                          href="https://www.kapre.com/real-estate/florida/licensing-courses" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-orange-600 font-medium">Kaplan Real Estate Education</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.theceshop.com/florida/pre-licensing/fl-salesperson-pre-licensing-cost" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-orange-600 font-medium">The CE Shop</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.colibrirealestate.com/real-estate/florida/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-orange-600 font-medium">Colibri Real Estate</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.realestateexpress.com/florida-real-estate-license" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-orange-600 font-medium">Real Estate Express</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 - Florida */}
                  <div className="border-l-4 border-green-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">2</div>
                      <h2 className="text-2xl font-bold text-gray-800">Meet Age and Education Requirements</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      You must be at least 18 years old and have a high school diploma or equivalent (GED) to apply for a Florida real estate license.
                    </p>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Required Documents:</h3>
                      <ul className="text-gray-700 space-y-1">
                        <li>• High school diploma or GED certificate</li>
                        <li>• Government-issued photo ID</li>
                        <li>• Social Security Number</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 3 - Florida */}
                  <div className="border-l-4 border-purple-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">3</div>
                      <h2 className="text-2xl font-bold text-gray-800">Pass the State Exam</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      After completing your 63-hour course, you will take the official state exam administered by Pearson VUE. The exam consists of 100 multiple-choice questions.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">Pearson VUE Information</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          The exam is computer-based and covers Florida real estate law, principles, and practices.
                        </p>
                        <a 
                          href="https://home.pearsonvue.com/fl/realestate" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          📖 Learn More Here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">Register for the Exam</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          You can register directly on the Pearson VUE website.
                        </p>
                        <a 
                          href="https://home.pearsonvue.com/fl/realestate" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          🎯 Register here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 - Florida */}
                  <div className="border-l-4 border-red-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">4</div>
                      <h2 className="text-2xl font-bold text-gray-800">Apply for Your License</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      After passing the exam, you have 2 years to apply for your license. You can apply online through the Florida Department of Business and Professional Regulation (DBPR) website.
                    </p>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">Application Requirements:</h3>
                      <ul className="text-gray-700 space-y-1">
                        <li>• Completed application form</li>
                        <li>• Exam score report</li>
                        <li>• Course completion certificate</li>
                        <li>• Application fee ($89.75)</li>
                        <li>• Fingerprints for background check</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 5 - Florida */}
                  <div className="border-l-4 border-indigo-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">5</div>
                      <h2 className="text-2xl font-bold text-gray-800">Find a Sponsoring Broker</h2>
                    </div>
                    <p className="text-gray-700">
                      To activate your license and begin working, you must be affiliated with a licensed Florida sponsoring broker. Your license will remain inactive until you're associated with a broker.
                    </p>
                  </div>

                  {/* Step 6 - Florida */}
                  <div className="border-l-4 border-teal-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">6</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete Continuing Education</h2>
                    </div>
                    <p className="text-gray-700">
                      Florida requires 14 hours of continuing education every 2 years to maintain your license. This includes 3 hours of core law and 11 hours of specialty courses.
                    </p>
                  </div>
                </>
              ) : isCalifornia ? (
                // California Content
                <>
                  {/* Step 1 - California */}
                  <div className="border-l-4 border-yellow-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-yellow-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">1</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete the 135-Hour Pre-Licensing Course</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      The state of California requires all aspiring salespersons to complete a 135-hour course from a California Department of Real Estate (DRE) approved school.
                    </p>
                    
                    <div className="bg-yellow-50 p-4 rounded-lg mb-4">
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">Find an Approved School:</h3>
                      <p className="text-gray-700 mb-3">
                        You must choose a school from the official DRE-approved list to ensure your course is valid.
                      </p>
                      <div className="bg-white p-3 rounded border">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Official DRE List:</strong> Check the official, up-to-date list of all approved schools directly from the California DRE.
                        </p>
                        <a 
                          href="https://www.dre.ca.gov/consumers/schools" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-yellow-600 hover:text-yellow-800 font-medium"
                        >
                          📄 Find Approved Schools Here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Popular DRE-Approved Online Providers:</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <a 
                          href="https://www.kapre.com/real-estate/california/licensing-courses" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-yellow-600 font-medium">Kaplan Real Estate Education</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.theceshop.com/california/pre-licensing/ca-salesperson-pre-licensing-cost" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-yellow-600 font-medium">The CE Shop</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.colibrirealestate.com/real-estate/california/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-yellow-600 font-medium">Colibri Real Estate</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.realestateexpress.com/california-real-estate-license" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-yellow-600 font-medium">Real Estate Express</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 - California */}
                  <div className="border-l-4 border-green-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">2</div>
                      <h2 className="text-2xl font-bold text-gray-800">Meet Age and Education Requirements</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      You must be at least 18 years old and have a high school diploma or equivalent (GED) to apply for a California real estate license.
                    </p>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Required Documents:</h3>
                      <ul className="text-gray-700 space-y-1">
                        <li>• High school diploma or GED certificate</li>
                        <li>• Government-issued photo ID</li>
                        <li>• Social Security Number</li>
                        <li>• Proof of legal presence in the United States</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 3 - California */}
                  <div className="border-l-4 border-purple-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">3</div>
                      <h2 className="text-2xl font-bold text-gray-800">Pass the State Exam</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      After completing your 135-hour course, you will take the official state exam administered by PSI. The exam consists of 150 multiple-choice questions.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">PSI Candidate Handbook</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          The handbook has all the details on the exam format, content, and how to register.
                        </p>
                        <a 
                          href="https://candidate.psiexams.com/catalog/display_program_info.jsp?programid=1690" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          📖 Find it here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">Register for the Exam</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          You can register directly on the PSI website.
                        </p>
                        <a 
                          href="https://www.psiexams.com/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          🎯 Register here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 - California */}
                  <div className="border-l-4 border-red-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">4</div>
                      <h2 className="text-2xl font-bold text-gray-800">Apply for Your License</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      After passing the exam, you have 2 years to apply for your license. You can apply online through the California Department of Real Estate (DRE) website.
                    </p>
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">Application Requirements:</h3>
                      <ul className="text-gray-700 space-y-1">
                        <li>• Completed application form</li>
                        <li>• Exam score report</li>
                        <li>• Course completion certificate</li>
                        <li>• Application fee ($245)</li>
                        <li>• Fingerprints for background check</li>
                        <li>• Proof of legal presence in the United States</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 5 - California */}
                  <div className="border-l-4 border-indigo-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">5</div>
                      <h2 className="text-2xl font-bold text-gray-800">Find a Sponsoring Broker</h2>
                    </div>
                    <p className="text-gray-700">
                      To activate your license and begin working, you must be affiliated with a licensed California sponsoring broker. Your license will remain inactive until you're associated with a broker.
                    </p>
                  </div>

                  {/* Step 6 - California */}
                  <div className="border-l-4 border-teal-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">6</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete Continuing Education</h2>
                    </div>
                    <p className="text-gray-700">
                      California requires 45 hours of continuing education every 4 years to maintain your license. This includes 18 hours of core courses and 27 hours of elective courses.
                    </p>
                  </div>
                </>
              ) : isTexas ? (
                // Texas Content
                <>
                  {/* Step 1 - Texas */}
                  <div className="border-l-4 border-red-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">1</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete the 180-Hour Pre-Licensing Course</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      The state of Texas requires all aspiring sales agents to complete a 180-hour course from a Texas Real Estate Commission (TREC) approved school.
                    </p>
                    
                    <div className="bg-red-50 p-4 rounded-lg mb-4">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">Find an Approved School:</h3>
                      <p className="text-gray-700 mb-3">
                        You must choose a school from the official TREC-approved list to ensure your course is valid.
                      </p>
                      <div className="bg-white p-3 rounded border">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Official TREC List:</strong> Check the official, up-to-date list of all approved schools directly from the Texas Real Estate Commission.
                        </p>
                        <a 
                          href="https://www.trec.texas.gov/education/approved-schools" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-red-600 hover:text-red-800 font-medium"
                        >
                          📄 Find Approved Schools Here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Popular TREC-Approved Online Providers:</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <a 
                          href="https://www.kapre.com/real-estate/texas/licensing-courses" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-red-600 font-medium">Kaplan Real Estate Education</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.theceshop.com/texas/pre-licensing/tx-salesperson-pre-licensing-cost" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-red-600 font-medium">The CE Shop</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.colibrirealestate.com/real-estate/texas/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-red-600 font-medium">Colibri Real Estate</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.realestateexpress.com/texas-real-estate-license" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-red-600 font-medium">Real Estate Express</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 - Texas */}
                  <div className="border-l-4 border-green-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">2</div>
                      <h2 className="text-2xl font-bold text-gray-800">Meet Age and Education Requirements</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      You must be at least 18 years old and have a high school diploma or equivalent (GED) to apply for a Texas real estate license.
                    </p>
                    <div className="bg-green-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Required Documents:</h3>
                      <ul className="text-gray-700 space-y-1">
                        <li>• High school diploma or GED certificate</li>
                        <li>• Government-issued photo ID</li>
                        <li>• Social Security Number</li>
                        <li>• Proof of legal presence in the United States</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 3 - Texas */}
                  <div className="border-l-4 border-purple-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">3</div>
                      <h2 className="text-2xl font-bold text-gray-800">Pass the State Exam</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      After completing your 180-hour course, you will take the official state exam administered by Pearson VUE. The exam consists of 125 multiple-choice questions (80 national + 45 Texas state).
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">Pearson VUE Information</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          The exam is computer-based and covers both national real estate principles and Texas-specific laws.
                        </p>
                        <a 
                          href="https://home.pearsonvue.com/tx/realestate" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          📖 Learn More Here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">Register for the Exam</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          You can register directly on the Pearson VUE website.
                        </p>
                        <a 
                          href="https://home.pearsonvue.com/tx/realestate" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          🎯 Register here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 - Texas */}
                  <div className="border-l-4 border-blue-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">4</div>
                      <h2 className="text-2xl font-bold text-gray-800">Apply for Your License</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      After passing the exam, you have 1 year to apply for your license. You can apply online through the Texas Real Estate Commission (TREC) website.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">Application Requirements:</h3>
                      <ul className="text-gray-700 space-y-1">
                        <li>• Completed application form</li>
                        <li>• Exam score report</li>
                        <li>• Course completion certificate</li>
                        <li>• Application fee ($205)</li>
                        <li>• Fingerprints for background check</li>
                        <li>• Proof of legal presence in the United States</li>
                      </ul>
                    </div>
                  </div>

                  {/* Step 5 - Texas */}
                  <div className="border-l-4 border-indigo-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">5</div>
                      <h2 className="text-2xl font-bold text-gray-800">Find a Sponsoring Broker</h2>
                    </div>
                    <p className="text-gray-700">
                      To activate your license and begin working, you must be affiliated with a licensed Texas sponsoring broker. Your license will remain inactive until you're associated with a broker.
                    </p>
                  </div>

                  {/* Step 6 - Texas */}
                  <div className="border-l-4 border-teal-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">6</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete Continuing Education</h2>
                    </div>
                    <p className="text-gray-700">
                      Texas requires 18 hours of continuing education every 2 years to maintain your license. This includes 10 hours of legal updates and 8 hours of elective courses.
                    </p>
                  </div>
                </>
              ) : isNewJersey ? (
                <>
                  {/* Step 1 - New Jersey */}
                  <div className="border-l-4 border-emerald-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-emerald-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">1</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete the 75-Hour Prelicensure Course</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      New Jersey requires a 75-hour approved prelicensure course before you can sit for the salesperson exam. Complete your education through an approved provider.
                    </p>
                    <a href="https://www.nj.gov/dobi/division_rec/realestate.htm" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-emerald-600 hover:text-emerald-800 font-medium">
                      NJ Real Estate Commission
                      <svg className="w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    </a>
                  </div>
                  {/* Step 2 - New Jersey */}
                  <div className="border-l-4 border-green-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">2</div>
                      <h2 className="text-2xl font-bold text-gray-800">Meet Age and Education Requirements</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      You must be at least 18 years old and have a high school diploma or equivalent. You must apply through a sponsoring employing broker after passing the exam.
                    </p>
                  </div>
                  {/* Step 3 - New Jersey */}
                  <div className="border-l-4 border-purple-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">3</div>
                      <h2 className="text-2xl font-bold text-gray-800">Pass the New Jersey Salesperson Exam</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      Pass the state-approved salesperson examination. Exam format and passing score are set by the New Jersey Real Estate Commission.
                    </p>
                  </div>
                  {/* Step 4 - New Jersey */}
                  <div className="border-l-4 border-teal-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">4</div>
                      <h2 className="text-2xl font-bold text-gray-800">Apply for Your License Through a Sponsoring Broker</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      A New Jersey salesperson does not practice independently; you must be hired by a licensed employing broker who sponsors your application.
                    </p>
                  </div>
                  {/* Step 5 - New Jersey */}
                  <div className="border-l-4 border-amber-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">5</div>
                      <h2 className="text-2xl font-bold text-gray-800">Maintain Your License</h2>
                    </div>
                    <p className="text-gray-700">
                      New Jersey licenses are valid for two years. You must complete 12 continuing education credit hours per term, including at least 2 ethics credits and other core topics (e.g., agency, Fair Housing/NJLAD).
                    </p>
                  </div>
                </>
              ) : isNewYork ? (
                <>
                  {/* Step 1 - New York */}
                  <div className="border-l-4 border-indigo-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-indigo-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">1</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete the 77-Hour Qualifying Education Course</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      New York requires all aspiring salespersons to complete 77 hours of approved qualifying education from a New York Department of State approved school.
                    </p>
                    <div className="bg-indigo-50 p-4 rounded-lg mb-4">
                      <h3 className="text-lg font-semibold text-indigo-800 mb-2">Find an Approved School:</h3>
                      <p className="text-gray-700 mb-3">
                        Your course must be from a school approved by the New York Department of State.
                      </p>
                      <a 
                        href="https://dos.ny.gov/real-estate-salesperson" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium"
                      >
                        📄 NY Department of State – Real Estate Salesperson
                        <svg className="w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                      </a>
                    </div>
                  </div>

                  {/* Step 2 - New York */}
                  <div className="border-l-4 border-green-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">2</div>
                      <h2 className="text-2xl font-bold text-gray-800">Meet Age and ID Requirements</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      You must be at least 18 years old and have a current New York photo driver&apos;s license or non-driver ID card.
                    </p>
                  </div>

                  {/* Step 3 - New York */}
                  <div className="border-l-4 border-purple-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">3</div>
                      <h2 className="text-2xl font-bold text-gray-800">Pass the New York Salesperson Exam</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      You must pass the NYS Real Estate Salesperson examination administered by PSI. The exam has 75 multiple-choice questions with a 90-minute time limit. A 70% passing score (53 correct) is required. Exam fee is $15.
                    </p>
                    <a href="https://www.psiexams.com/real-estate/new-york" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium">
                      Schedule exam at PSI
                      <svg className="w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                    </a>
                  </div>

                  {/* Step 4 - New York */}
                  <div className="border-l-4 border-teal-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-teal-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">4</div>
                      <h2 className="text-2xl font-bold text-gray-800">Apply for Your License</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      The initial application fee is $65. You must be sponsored by a licensed New York broker before your license can be issued. Salespersons cannot operate independently; they must work under broker supervision.
                    </p>
                  </div>

                  {/* Step 5 - New York */}
                  <div className="border-l-4 border-amber-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-amber-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">5</div>
                      <h2 className="text-2xl font-bold text-gray-800">Maintain Your License</h2>
                    </div>
                    <p className="text-gray-700">
                      New York licenses are valid for two years. You must complete 22.5 hours of approved continuing education for renewal, including mandated topics such as fair housing, agency, ethics, cultural competency, implicit bias, and recent legal matters.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* Step 1 - Georgia */}
                  <div className="border-l-4 border-blue-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">1</div>
                      <h2 className="text-2xl font-bold text-gray-800">Complete the 75-Hour Pre-Licensing Course</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      The state of Georgia requires all aspiring agents to complete a 75-hour course from a Georgia Real Estate Commission (GREC) approved school.
                    </p>
                    
                    <div className="bg-blue-50 p-4 rounded-lg mb-4">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">Find an Approved School:</h3>
                      <p className="text-gray-700 mb-3">
                        You must choose a school from the official GREC-approved list to ensure your course is valid.
                      </p>
                      <div className="bg-white p-3 rounded border">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Official GREC List:</strong> Check the official, up-to-date list of all approved schools directly from the source.
                        </p>
                        <a 
                          href="https://grec.state.ga.us/wp-content/uploads/pdfs/RE%20Approved%20Schools%20List.pdf" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium"
                        >
                          📄 Download the PDF Here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="text-lg font-semibold text-gray-800 mb-3">Popular GREC-Approved Online Providers:</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <a 
                          href="https://www.kapre.com/real-estate/georgia/licensing-courses" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-blue-600 font-medium">Kaplan Real Estate Education</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.theceshop.com/georgia/pre-licensing/ga-salesperson-pre-licensing-cost" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-blue-600 font-medium">The CE Shop</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                        <a 
                          href="https://www.colibrirealestate.com/real-estate/georgia/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center p-3 bg-white rounded-lg border hover:shadow-md transition-shadow"
                        >
                          <span className="text-blue-600 font-medium">Colibri Real Estate</span>
                          <svg className="w-4 h-4 ml-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 2 - Georgia */}
                  <div className="border-l-4 border-green-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">2</div>
                      <h2 className="text-2xl font-bold text-gray-800">Get a Criminal History Report</h2>
                    </div>
                    <p className="text-gray-700">
                      You will need a certified criminal history report from the Georgia Crime Information Center (GCIC) or your resident state. This report must be dated no more than 60 days before you apply for your license.
                    </p>
                  </div>

                  {/* Step 3 - Georgia */}
                  <div className="border-l-4 border-purple-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-purple-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">3</div>
                      <h2 className="text-2xl font-bold text-gray-800">Pass the State Exam</h2>
                    </div>
                    <p className="text-gray-700 mb-4">
                      After completing your 75-hour course, you will take the official state exam administered by PSI.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">PSI Candidate Handbook</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          The handbook has all the details on the exam format, content, and how to register.
                        </p>
                        <a 
                          href="https://candidate.psiexams.com/catalog/display_program_info.jsp?programid=1690" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          📖 Find it here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h3 className="text-lg font-semibold text-purple-800 mb-2">Register for the Exam</h3>
                        <p className="text-sm text-gray-600 mb-3">
                          You can register directly on the PSI website.
                        </p>
                        <a 
                          href="https://www.psiexams.com/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-purple-600 hover:text-purple-800 font-medium"
                        >
                          🎯 Register here
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </div>

                  {/* Step 4 - Georgia */}
                  <div className="border-l-4 border-orange-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-orange-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">4</div>
                      <h2 className="text-2xl font-bold text-gray-800">Find a Sponsoring Broker</h2>
                    </div>
                    <p className="text-gray-700">
                      To activate your license and begin working, you must be affiliated with a licensed Georgia sponsoring broker.
                    </p>
                  </div>

                  {/* Step 5 - Georgia */}
                  <div className="border-l-4 border-red-500 pl-6">
                    <div className="flex items-center mb-4">
                      <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold mr-4">5</div>
                      <h2 className="text-2xl font-bold text-gray-800">Apply for Your License</h2>
                    </div>
                    <p className="text-gray-700">
                      Once you have passed the exam, you have 12 months to submit your license application at a PSI testing center. Make sure you have all the required documents with you, including your criminal history report.
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className={`mt-12 p-6 bg-gradient-to-r ${isFlorida ? 'from-orange-50 to-red-50' : isCalifornia ? 'from-yellow-50 to-orange-50' : isTexas ? 'from-red-50 to-blue-50' : isNewYork ? 'from-blue-50 to-indigo-50' : isNewJersey ? 'from-emerald-50 to-teal-50' : 'from-green-50 to-blue-50'} rounded-lg border ${isFlorida ? 'border-orange-200' : isCalifornia ? 'border-yellow-200' : isTexas ? 'border-red-200' : isNewYork ? 'border-indigo-200' : isNewJersey ? 'border-emerald-200' : 'border-green-200'}`}>
              <p className="text-lg text-gray-800 text-center">
                <span className="font-semibold">🎉 Your flashcards are preparing you for the knowledge portion</span>—now follow these steps to turn your studying into a successful career!
              </p>
            </div>

            {/* Back to Dashboard */}
            <div className="mt-8 text-center">
              <Link 
                to="/dashboard" 
                className={`inline-flex items-center px-6 py-3 bg-gradient-to-r ${isFlorida ? 'from-orange-600 to-red-600' : isCalifornia ? 'from-yellow-600 to-orange-600' : isTexas ? 'from-red-600 to-blue-600' : isNewYork ? 'from-blue-600 to-indigo-600' : isNewJersey ? 'from-emerald-600 to-teal-600' : 'from-blue-600 to-purple-600'} text-white font-semibold rounded-lg hover:shadow-lg transition-all transform hover:scale-105`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicensingGuide;
